import { ToolSearch, ToolFilters, ToolButton, ToolEmpty } from "./ToolWorkspace";
import { useState, useEffect, useRef } from "react";
import { collection, query, onSnapshot, doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { C } from "../constants/theme";
import { toast } from "../hooks/useToast";
import { useConfirm } from "../hooks/useConfirm";
import { mudarStatusPedidoOnline } from "../utils/pedidosPersistencia";
import { fmtMoeda } from "../utils/format";
import { Card } from "./ui/Card";
import { Btn } from "./ui/Btn";
import { ChatPedido } from "./ChatPedido";
import { pedidoOnlineOculto } from "../constants/statusPedido";
import { mpHabilitado } from "../utils/mercadoPago";
import { AlertasMercadoPago, textoAlertaMP, tituloAlertaMP } from "./configuracoes/AlertasMercadoPago";

// Status que indicam pedido confirmado (já pode baixar estoque)


const STATUS = [
  { id: "pagamento_em_analise", label: "Conferir pagamento", cor: C.warning },
  { id: "pago", label: "Pagamento confirmado", cor: "#22c55e" },
  { id: "imprimindo", label: "Imprimindo", cor: "#3b82f6" },
  { id: "pronto", label: "Pronto", cor: "#f0a830" },
  { id: "em_entrega", label: "Saiu para entrega", cor: "#0ea5e9" },
  { id: "concluido", label: "Concluído", cor: C.success },
  { id: "cancelado", label: "Cancelado", cor: C.danger }
];

export function PedidosOnline({ vendedorId }) {
  const [busca,setBusca] = useState("");
  const [filtro,setFiltro] = useState("todos");
  const [pedidos, setPedidos] = useState([]);
  const [aberto, setAberto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [excluindo, setExcluindo] = useState(null);
  const alterandoRef = useRef(false);
  const [confirmar, modalConfirm] = useConfirm();

  useEffect(() => {
    if (!vendedorId) return;
    const q = query(collection(db, `users/${vendedorId}/pedidos_online`));
    const unsub = onSnapshot(q, (snap) => {
      // Checkout do Mercado Pago ainda não pago não é pedido para a loja — a não
      // ser que o Mercado Pago tenha algo para o vendedor conferir (mp_alerta)
      const lista = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(p => !pedidoOnlineOculto(p) || p.mp_alerta);
      lista.sort((a, b) => (b.updated_at || b.ts || 0) - (a.updated_at || a.ts || 0));
      setPedidos(lista);
      setLoading(false);
      // mantém pedido aberto atualizado
      setAberto(prev => prev ? (lista.find(p => p.id === prev.id) || prev) : null);
    }, (e) => { console.error("Erro pedidos online:", e); setLoading(false); });
    return () => unsub();
  }, [vendedorId]);

  const mudarStatus = async (pedido, status) => {
    if (alterandoRef.current) return;
    alterandoRef.current = true; setExcluindo(pedido.id);
    try {
      const r = await mudarStatusPedidoOnline(vendedorId, pedido, status);
      r.avisos?.forEach(aviso => toast(aviso, "warning"));
    } catch (e) { toast(e.message || "Não foi possível atualizar o pedido.", "error"); }
    finally { alterandoRef.current = false; setExcluindo(null); }
  };

  const abrirPedido = async (p) => {
    setAberto(p);
    if (p.naoLidoVendedor) {
      try { await updateDoc(doc(db, `users/${vendedorId}/pedidos_online/${p.id}`), { naoLidoVendedor: false }); }
      catch {}
    }
  };

  const excluirPedido = async p => {
    if (await confirmar("Cancelar este pedido? A compra e a conversa serão mantidas no histórico. Recebimentos podem ser estornados na área Pedidos.")) await mudarStatus(p, "cancelado");
  };

  const statusInfo = (id) => STATUS.find(s => s.id === id) || STATUS[0];

  // ── Detalhe + chat de um pedido ──
  const filtrados = pedidos.filter(item => [item.id,item.cliente?.nome,item.cliente?.email,item.ultimaMsg].join(" ").toLowerCase().includes(busca.toLowerCase().trim()) && (filtro === "todos" || (filtro === "naolidos" ? item.naoLidoVendedor : item.status === filtro)));

  if (aberto) {
    return (
      <div className="site-detail" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {modalConfirm}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
          <button onClick={() => setAberto(null)} style={{
            background: "none", border: "none", color: C.accent2, cursor: "pointer",
            fontSize: 14
          }}>← Voltar aos pedidos</button>
          <Btn small outline color={C.danger} disabled={excluindo === aberto.id || aberto.status === "cancelado"} onClick={() => excluirPedido(aberto)}>
            {excluindo === aberto.id ? "Salvando…" : "Cancelar pedido"}
          </Btn>
        </div>

        <Card style={{ padding: 18 }}>
          <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16 }}>Pedido #{aberto.id.slice(-5)}</div>
              <div style={{ fontSize: 13, color: C.muted, marginTop: 2 }}>
                👤 {aberto.cliente?.nome} · {aberto.cliente?.celular || aberto.cliente?.email}
              </div>
            </div>
            <div style={{ fontWeight: 900, fontSize: 20, color: C.success }}>{fmtMoeda(aberto.total)}</div>
          </div>

          {/* Itens */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
            {(aberto.itens || []).map((i, idx) => (
              <div key={idx} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: C.text }}>
                <span>{i.qtd}× {i.nome} {i.encomenda ? "🏷️" : ""}</span>
                <span style={{ color: C.muted }}>{fmtMoeda((i.preco || 0) * (i.qtd || 0))}</span>
              </div>
            ))}
          </div>

          {aberto.mp_alerta && (
            <div role="note" style={{ background: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.45)", borderRadius: 10, padding: "10px 14px", marginBottom: 12, fontSize: 12.5, color: C.text, lineHeight: 1.6 }}>
              <strong>⚠️ {tituloAlertaMP(aberto.mp_alerta)} no Mercado Pago.</strong> {textoAlertaMP(aberto.mp_alerta)}
            </div>
          )}

          {/* Pagamento */}
          <div style={{ background: C.panel, borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: 12.5, color: C.text, lineHeight: 1.7 }}>
            <div>
              Pagamento: {aberto.pagamento?.metodo === "pix" ? "PIX" : aberto.pagamento?.metodo === "cartao" ? "Cartão" : "—"}
              {aberto.pagamento?.status === "em_analise" && aberto.status === "pagamento_em_analise" ? " (conferir)" : ""}
            </div>
          </div>

          {/* Status */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {STATUS.map(s => (
              <button key={s.id} onClick={() => mudarStatus(aberto, s.id)} style={{
                padding: "5px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: "pointer",
                border: `1px solid ${s.cor}`,
                background: aberto.status === s.id ? s.cor : "transparent",
                color: aberto.status === s.id ? "#0a1628" : s.cor
              }}>{s.label}</button>
            ))}
          </div>
        </Card>

        <Card style={{ padding: 18 }}>
          <h3 style={{ margin: "0 0 12px", fontSize: 15 }}>💬 Conversa com o cliente</h3>
          <ChatPedido vendedorId={vendedorId} pedidoId={aberto.id} autor="vendedor" altura={340} />
        </Card>
      </div>
    );
  }

  // ── Lista de pedidos ──
  return <div className="tool-module site-inbox">
    {modalConfirm}{mpHabilitado()&&<AlertasMercadoPago uid={vendedorId} />}
    <ToolSearch value={busca} onChange={setBusca} placeholder="Buscar pedido ou cliente" />
    <ToolFilters value={filtro} onChange={setFiltro} options={[{value:"todos",label:"Todos",count:pedidos.length},{value:"naolidos",label:"Não lidos",count:pedidos.filter(p=>p.naoLidoVendedor).length},...STATUS.map(s=>({value:s.id,label:s.label}))]} />
    <div className="tool-item-grid">{loading?<ToolEmpty icon="notas" title="Carregando pedidos…" />:!filtrados.length?<ToolEmpty icon="notas" title={pedidos.length?"Nenhum pedido neste filtro":"Sua próxima venda aparece aqui"}>Acompanhe os pedidos feitos na loja e converse com cada cliente.</ToolEmpty>:filtrados.map(p=>{const st=statusInfo(p.status);return <article className="tool-item" key={p.id}>
      <div className="site-inbox-heading"><span className="tool-eyebrow">PEDIDO #{p.id.slice(-5)}</span>{p.naoLidoVendedor&&<span className="site-unread">Novo</span>}</div><h3>{p.cliente?.nome||"Cliente"}</h3><span className="tool-status">{st.label}</span>
      {p.mp_alerta&&<p role="note">⚠ {tituloAlertaMP(p.mp_alerta)} no Mercado Pago — confira antes de enviar.</p>}
      <div className="tool-item-metrics"><span>Valor<strong>{fmtMoeda(p.total)}</strong></span><span>Quantidade<strong>{(p.itens||[]).reduce((n,i)=>n+(i.qtd||0),0)} itens</strong></span></div>{p.ultimaMsg&&<p className="site-message-preview">{p.ultimaMsgAutor==="vendedor"?"Você: ":""}{p.ultimaMsg}</p>}
      <div className="tool-actions"><ToolButton secondary onClick={()=>abrirPedido(p)}>Ver pedido e conversa</ToolButton><ToolButton danger disabled={excluindo===p.id||p.status==="cancelado"} onClick={()=>excluirPedido(p)}>{excluindo===p.id?"Salvando…":"Cancelar pedido"}</ToolButton></div>
    </article>;})}</div>
  </div>;
}
